import { Link } from 'react-router-dom';

/**
 * Hero section component for landing page
 * Displays main headline, value proposition, and social proof
 * @param {Object} props - Component props
 * @param {number} props.spotsRemaining - Number of beta spots remaining
 * @param {Object} props.stats - User statistics (userCount, reviewCount, avgRating)
 */
function HeroSection({ spotsRemaining, stats }) {
  return (
    <section className="relative overflow-hidden gradient-hero">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-6 pb-5 sm:pt-8 sm:pb-14 lg:pt-10 lg:pb-18">
        <div className="text-center">
          {/* Beta badge with spots remaining */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
            <p className="beta-badge inline-flex items-center gap-2 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-full px-4 py-2 shadow-lg">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 2l3 7h7l-5.5 4 2 7L12 17l-6.5 4 2-7L2 10h7l3-7z" fill="currentColor" opacity="0.9"/>
              </svg>
              Beta is live — Only <span className="spots-remaining">{spotsRemaining}</span> Founder Spots Left!
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 2l3 7h7l-5.5 4 2 7L12 17l-6.5 4 2-7L2 10h7l3-7z" fill="currentColor" opacity="0.9"/>
              </svg>
            </p>
          </div>

          {/* Main headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight text-white leading-tight mb-6">
            AI-Powered Diagnostic for Tutors
          </h1>
          
          {/* Primary value proposition */}
          <p className="mt-5 text-lg sm:text-xl lg:text-2xl text-white/95 max-w-3xl mx-auto leading-relaxed font-semibold">
            Pinpoint Every Student's Knowledge Gaps — And discover what they actually need to cover
          </p>
          
          {/* Supporting text */}
          <p className="mt-4 text-base sm:text-lg lg:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
            Save hours on prep work while delivering more targeted, effective lessons to each student.
          </p>

          {/* Incentive text */}
          <p className="mt-5 text-sm sm:text-base text-amber-200 font-semibold max-w-3xl mx-auto leading-relaxed">
            First 20 tutors who join get Wurlo free for life. Everyone after that still gets their first 10 students on Wurlo for free — no caps enforced, we simply honor it.
          </p>

          {/* Social proof section */}
          <div id="social-proof" className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-white/90">
            {stats.userCount > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center text-xs font-bold">👤</div>
                  <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center text-xs font-bold">👤</div>
                  <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center text-xs font-bold">👤</div>
                </div>
                <span className="text-sm font-semibold">{stats.userCount}+ tutors teaching smarter</span>
              </div>
            )}
            {stats.reviewCount > 0 && stats.avgRating > 0 && (
              <div className="flex items-center gap-1.5">
                <svg className="w-5 h-5 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-sm font-semibold">{stats.avgRating}/5 rating</span>
              </div>
            )}
          </div>

          {/* Call to action button */}
          <div className="mt-8 flex flex-col items-center gap-6">
            <a href="#waitlist" className="group relative inline-flex items-center justify-center gap-3 rounded-2xl bg-white px-10 py-5 text-[#4F46E5] text-xl sm:text-2xl font-extrabold hover:bg-gray-50 focus-ring shadow-2xl transform hover:scale-105 transition-all overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="relative z-10 text-amber-500" aria-hidden="true">
                <path d="M12 2l3 7h7l-5.5 4 2 7L12 17l-6.5 4 2-7L2 10h7l3-7z"/>
              </svg>
              <span className="relative z-10">Get Instant Beta Access</span>
            </a>
          </div>

          {/* Feature highlights */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-white/90">
            <p className="flex items-center gap-2 font-medium">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Instant diagnostic insights for every student
            </p>
            <p className="flex items-center gap-2 font-medium">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Deep Understanding of your sudents' knowledge gaps
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
